import { useState, useEffect, useRef, useCallback } from 'react';

import { csrfTokenHeader } from '../services/CsrfTokenService';

interface UseNovaPoshtaSearchOptions {
    url: string;
    params?: Record<string, string>;
    minLength?: number;
    debounceMs?: number;
    enabled?: boolean;
}

interface UseNovaPoshtaSearchResult<T> {
    results: T[];
    isLoading: boolean;
    isFallback: boolean;
    search: (query: string) => void;
    clear: () => void;
}

export function useNovaPoshtaSearch<T>(
    options: UseNovaPoshtaSearchOptions,
): UseNovaPoshtaSearchResult<T> {
    const {
        url,
        params = {},
        minLength = 2,
        debounceMs = 400,
        enabled = true,
    } = options;

    const [results, setResults] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFallback, setIsFallback] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const clear = useCallback(() => {
        setResults([]);
        setIsLoading(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (abortRef.current) abortRef.current.abort();
    }, []);

    const search = useCallback(
        (query: string) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (abortRef.current) abortRef.current.abort();

            if (!enabled || query.trim().length < minLength) {
                setResults([]);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);

            timerRef.current = setTimeout(async () => {
                const controller = new AbortController();
                abortRef.current = controller;

                try {
                    const searchParams = new URLSearchParams({
                        ...params,
                        q: query.trim(),
                    });
                    const res = await fetch(
                        `${url}?${searchParams.toString()}`,
                        {
                            signal: controller.signal,
                            headers: {
                                Accept: 'application/json',
                                ...csrfTokenHeader(),
                            },
                        },
                    );

                    if (!res.ok) {
                        setIsFallback(true);
                        setResults([]);
                        return;
                    }

                    const json = await res.json();

                    if (json.fallback) {
                        setIsFallback(true);
                        setResults([]);
                    } else {
                        setIsFallback(false);
                        setResults(json.data || []);
                    }
                } catch (err) {
                    if ((err as Error).name !== 'AbortError') {
                        setIsFallback(true);
                        setResults([]);
                    }
                } finally {
                    setIsLoading(false);
                }
            }, debounceMs);
        },
        [url, JSON.stringify(params), minLength, debounceMs, enabled],
    );

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, []);

    return { results, isLoading, isFallback, search, clear };
}
